/* eslint-disable max-lines */
import qs from 'qs';
import { isOpera } from 'react-device-detect';
import { StatusCodes } from 'http-status-codes';
import Jink from '@streaming/jink';

import ConfigurationService, { ConfigurationKeys } from 'services/ConfigurationService';
import CookieStorageService from 'services/CookieStorageService';
import FirebaseService from 'services/FirebaseService';
import { translate } from 'services/i18nService';
import {
  BROADCASTER_DEVICES_RECEIVED,
  BROADCASTING_IN_HD,
  END_BROADCAST_ERROR,
  INIT_STREAMING_ROOM_RESPONSE,
  MIC_BLOCKED,
  MIC_LEVEL_VALUE,
  MIC_NOT_FOUND,
  PAUSE_STATUS_ERROR,
  PAUSE_STATUS_SUCCESS,
  RESET_SHOW_STATE,
  ROOM_LOADING_STATUS,
  SET_BROADCASTER_VIDEO_LOADING,
  SET_DEVICE_ERROR,
  SET_FPS_FROM_JINK,
  SET_STREAMING_JWT_TOKEN,
  SET_TAGS_FROM_BROADCASTER_SETTINGS_AND_GET_PREDEFINED_TAGS,
  START_BROADCAST_RESPONSE,
  STREAM_KEY_RESPONSE,
  TIPPERS_LEADERBOARD_DATA,
  UPDATE_AUDIO_DEVICE,
  UPDATE_VIDEO_DEVICE,
  UPDATE_VIDEO_METRICS,
  USER_BROADCAST_SETTINGS_ERROR,
  USER_BROADCAST_SETTINGS_RESPONSE,
  USER_HAS_MULTIPLE_IDENTITIES,
} from 'services/MessagingService/EventNames';
import messagingService from 'services/MessagingService/MessagingService';
import ModalService from 'services/ModalService';
import NotificationService from 'services/NotificationService';
import StreamingStore from 'stores/StreamingStore';
import FirebaseStore from 'stores/FirebaseStore';
import TracingService from 'services/TracingService';
import track from 'services/tracking';
import UserStore from 'stores/UserStore';
import APIStates from 'enum/APIStates';
import { PAUSE_BROADCAST_ACTIONS } from 'enum/pauseBroadcastActions';
import routeDefinitions from 'enum/routeDefinitions';
import allUserGroups from 'enum/userGroups';
import { MINUTE_AS_MILLISECONDS } from 'enum/timeFormat';
import ApiService from 'services/ApiService/ApiService';
import {
  BROADCAST_WINDOW_HEIGHT,
  BROADCAST_WINDOW_HEIGHT_OPERA,
  BROADCAST_WINDOW_WIDTH,
  DEVICE_ERRORS,
  STREAM_PROVIDERS,
  STREAM_PROTOCOL,
  STREAM_SOURCE_EXTERNAL,
} from 'utils/consts';
import parseCatch from 'utils/parseCatch';

import firebaseNode from '../firebase/firebaseNode';


const WEBCHAT_HOST = ConfigurationService.get(ConfigurationKeys.WEB_CHAT_HOST);
const TIPPING_BASE_URL = ConfigurationService.get(ConfigurationKeys.TIPPING_BASE_URL);
const BASE_URL = ConfigurationService.get(ConfigurationKeys.BASE_URL);

const BROADCAST_URL = `${WEBCHAT_HOST}/broadcast`;
const INIT_STREAMING_ROOM_URL = `${WEBCHAT_HOST}v2/streaming-rooms`;
const PAUSE_BROADCAST_URL = `${WEBCHAT_HOST}pauseBroadcast`;
const SEND_NOTIFICATION_URL = `${WEBCHAT_HOST}sendNotification`;
const STREAM_KEY_URL = `${WEBCHAT_HOST}stream-key`;
const USER_BROADCAST_SETTINGS_URL = `${WEBCHAT_HOST}userBroadcastSettings`;
const SET_LOADING_TIMEOUT = 500;
const CONNECTION_STATES = {
  CONNECTED: 'connected',
  DISCONNECTED: 'disconnected',
};

class StreamingService {
  constructor() {
    this.username = '';
    this.messagingService = messagingService();
    this.broadcasterWindow = null;
    this.jinkInstance = null;
    this.devices = [];
    this.selectedVideoId = '';
    this.videoIdFromBroadcastSettings = '';
    this.audioIdFromBroadcastSettings = '';
    this.broadcastId = null;
    this.qualityFactor = null;
    this.highestResolution = '';
    this.triesLeft = 10;
    this.slowInternetRetriesLeft = 3;
    this.errorTimeout = null;
    this.lastInitTimestamp = 0;
    this.successfullyInitiated = false;
    this.protocol = '';
    this.provider = '';
    this.sourceType = '';
    this.originId = '';
  }

  /**
   * Method for getting all devices and initializing flow
   * @param {boolean} isCeeBlue
   * @param {string} videoIdParam
   * @param {string} audioIdParam
   */
  getDevices = (isCeeBlue, videoIdParam, audioIdParam) => {
    Jink.listDevices().then((devices) => {
      const videoDeviceAvailable = devices.some((device) => device.kind === 'videoinput');
      const audioDeviceAvailable = devices.some((device) => device.kind === 'audioinput');
      if (videoDeviceAvailable) {
        this.onDevicesReceived(devices);
        const isSavedCamAvailable = devices.some((device) => device.deviceId === this.videoIdFromBroadcastSettings);
        let videoDeviceId = devices.find((device) => device.kind === 'videoinput')?.deviceId;

        if (isSavedCamAvailable && !videoIdParam) {
          videoDeviceId = this.videoIdFromBroadcastSettings;
        } else if (videoIdParam) {
          videoDeviceId = videoIdParam;
        } else {
          this.messagingService.emit(UPDATE_VIDEO_DEVICE, videoDeviceId);
        }

        this.setDevicesId(
          devices,
          isCeeBlue,
          videoDeviceId,
          audioIdParam
        );

        if (!audioDeviceAvailable) {
          this.messagingService.emit(MIC_NOT_FOUND);
        }
      } else {
        this.messagingService.emit(SET_DEVICE_ERROR, DEVICE_ERRORS.NO_CAMERA_AVAILABLE);
      }
    }).catch((error) => {
      this.messagingService.emit(SET_DEVICE_ERROR, DEVICE_ERRORS.NO_CAMERA_AVAILABLE);
      // eslint-disable-next-line no-console
      console.error(error);
    });
  };


  /**
   * Method for initalization jink library and setting listeners for events that jink emmits
   */
  initJink = (username, isCeeBlue) => {
    this.jinkInstance = new Jink();
    this.messagingService.emit(SET_DEVICE_ERROR, '');
    this.messagingService.emit(SET_BROADCASTER_VIDEO_LOADING, true);

    this.username = username;

    this.jinkInstance.setInfo({
      tenant: 'CAM4',
      username,
    });

    this.jinkInstance.addListener('PermissionDenied', (_, payload) => {
      if (!payload.constraints.video) {
        this.messagingService.emit(SET_DEVICE_ERROR, payload.error);
      } else {
        this.messagingService.emit(MIC_BLOCKED);
        this.startWithoutSound = true;
      }
    });

    this.getDevices(isCeeBlue);

    this.jinkInstance.addListener('ConnectionStateChanged', async(_, payload) => {
      if (payload.connectionState === CONNECTION_STATES.DISCONNECTED) {
        StreamingStore.setRoomDisconnected(true);
      } else if (payload.connectionState === 'failed') {
        if (this.triesLeft) {
          const retryDelay = 3000;
          await this.retryStreamPush(this.protocol, this.provider, null, this.sourceType, retryDelay);
        } else {
          StreamingStore.setBroadcastOver(true);
          ModalService.showModal(ModalService.MODAL_KEYS.BROADCAST_ERROR_MODAL);
          track({
            eventname: 'streamPushFailed',
            provider: this.provider,
            origin: this.originId,
            username,
            logLevel: 'ERROR',
          });
        }
      } else if (payload.connectionState === CONNECTION_STATES.CONNECTED) {
        StreamingStore.setRoomDisconnected(false);
      }
    });

    this.jinkInstance.addListener('AudioLevel', (_, payload) => {
      this.messagingService.emit(MIC_LEVEL_VALUE, payload.value);
    });
  };

  /**
   * Method for getting video and audio devices from jink library
   * @param {array} data
   * @emits BROADCASTER_DEVICES_RECEIVED emits the response for devices receieved
   */
  onDevicesReceived = (data) => {
    this.messagingService.emit(BROADCASTER_DEVICES_RECEIVED, data);
    this.devices = data;
    this.updateSelectedVideoId(this.videoIdFromBroadcastSettings);
  };

  /**
   * Method for finding saved video and audio device id
   * @param {array} devices
   * @param {bool} isCeeBlue
   * @param {string} videoDeviceIdParam
   * @param {string} audioIdParam
   */
  setDevicesId = (devices, isCeeBlue, videoDeviceIdParam, audioIdParam) => {
    const isSavedCamAvailable = devices.some((device) => device.deviceId === this.videoIdFromBroadcastSettings);
    const isSavedMicAvailable = devices.some((device) => device.deviceId === this.audioIdFromBroadcastSettings);
    let videoDeviceId = devices.find((device) => device.kind === 'videoinput')?.deviceId;
    let audioDeviceId = devices.find((device) => device.kind === 'audioinput')?.deviceId;
    if (isSavedCamAvailable && !videoDeviceIdParam) {
      videoDeviceId = this.videoIdFromBroadcastSettings;
    } else if (videoDeviceIdParam) {
      videoDeviceId = videoDeviceIdParam;
    } else {
      this.messagingService.emit(UPDATE_VIDEO_DEVICE, videoDeviceId);
    }
    if (isSavedMicAvailable && !audioIdParam) {
      audioDeviceId = this.audioIdFromBroadcastSettings;
    } else if (audioIdParam) {
      audioDeviceId = audioIdParam;
    } else {
      this.messagingService.emit(UPDATE_AUDIO_DEVICE, audioDeviceId);
    }

    this.setConstraintsAndInitStream(
      videoDeviceId,
      isCeeBlue,
      audioDeviceId,
    );
  };

  /**
   * Method for changing camera
   * @param {string} videoDeviceId
   * @param {bool} isCeeBlue
   * @param {string} string
   * @param {object} error
   */
  handleCameraChange = (videoDeviceId, error, isCeeBlue, audioDeviceId) => {
    if (error) {
      this.getDevices(isCeeBlue, videoDeviceId, audioDeviceId);
      this.messagingService.emit(SET_DEVICE_ERROR, '');

      return;
    }

    const data = {
      video: { deviceId: { exact: videoDeviceId } },
      audio: { deviceId: { exact: audioDeviceId } },
    };

    if (this.startWithoutSound) {
      data.audio = false;
    }

    this.jinkInstance.updateStream(data).then(() => {
      setTimeout(() => {
        this.messagingService.emit(SET_BROADCASTER_VIDEO_LOADING, false);
      }, SET_LOADING_TIMEOUT);
      this.videoIdFromBroadcastSettings = videoDeviceId;
    }).catch((err) => {
      // eslint-disable-next-line no-console
      console.error(err);

      this.messagingService.emit(SET_FPS_FROM_JINK, 0);
      this.messagingService.emit(MIC_LEVEL_VALUE, 0);
      this.messagingService.emit(SET_DEVICE_ERROR, DEVICE_ERRORS.NOT_READABLE_ERROR);
      this.messagingService.emit(SET_BROADCASTER_VIDEO_LOADING, false);

      this.jinkInstance.stopPush().then(() => this.jinkInstance.stopClientMedia()).catch((pushError) => {
        // eslint-disable-next-line no-console
        console.error(pushError);
      });
    });
  };

  /**
   * Stops pushing the stream to origin and stops the video feed from the camera
   * @returns {Promise}
   */
  stopPushAndResetClientMedia = async(isCeeBlue, needsReinit) => {
    this.messagingService.emit(SET_BROADCASTER_VIDEO_LOADING, true);
    await this.jinkInstance.stopPush();
    await this.jinkInstance.stopClientMedia();
    if (needsReinit) {
      await this.initJink(this.username, isCeeBlue);
    }
  };

  /**
   * Method for destroying jink
   * @param {function} cb The callback to be executed once everything is settled with the current jink instance
   */
  destroyJink = async() => {
    await this.stopPushAndResetClientMedia();
    this.videoIdFromBroadcastSettings = this.selectedVideoId;
    this.jinkInstance = null;
  };

  handleSwitchingOrigins = (originId, isCeeBlue) => {
    CookieStorageService.set('preferredOrigin', originId);
    this.jinkInstance.stopPush().then(() => {
      this.initStreamingRoom({
        protocol: STREAM_PROTOCOL.WEBRTC,
        provider: isCeeBlue ? STREAM_PROVIDERS.CEEBLUE : STREAM_PROVIDERS.WOWZA,
        originId,
      });
    }).catch((error) => {
      // eslint-disable-next-line no-console
      console.error(error);
    });
  };

  /**
   * Method for preparing constraints object and starting stream
   * @param {string} videoDeviceId
   * @param {boolean} isCeeBlue
   * @param {string} audioDeviceId
   */
  setConstraintsAndInitStream = async(videoDeviceId, isCeeBlue, audioDeviceId) => {
    if (StreamingStore.isStreamOver) {
      return;
    }

    const audioDeviceAvailable = this.devices.some((device) => device.kind === 'audioinput');
    const data = {
      videoElement: 'client-video',
      video: { deviceId: { exact: videoDeviceId } },
      audio: { deviceId: { exact: audioDeviceId } },
    };

    if (this.startWithoutSound || !audioDeviceAvailable) {
      data.audio = false;
    }

    try {
      const stream = await this.jinkInstance.startClientMedia(data);
      if (data.audio && !stream?.getAudioTracks?.()?.length) {
        const { devicesReceived, _handleDevicesReceived, handleMicBlocked } = StreamingStore;
        const newDevicesReceived = devicesReceived.filter((d) => d.kind !== 'audioinput');
        _handleDevicesReceived(newDevicesReceived);
        handleMicBlocked();
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
      this.messagingService.emit(SET_DEVICE_ERROR, DEVICE_ERRORS.NOT_READABLE_ERROR);

      return;
    }

    setTimeout(() => {
      this.messagingService.emit(SET_BROADCASTER_VIDEO_LOADING, false);
    }, SET_LOADING_TIMEOUT);
    this.initStreamingRoom({
      protocol: STREAM_PROTOCOL.WEBRTC,
      provider: isCeeBlue ? STREAM_PROVIDERS.CEEBLUE : STREAM_PROVIDERS.WOWZA,
    });
  };

  /**
   * Method for setting selectedVideoId
   * @param {string} id
   */
  updateSelectedVideoId = (id) => {
    this.messagingService.emit(SET_BROADCASTER_VIDEO_LOADING, true);
    const isDeviceAvailable = this.devices.some((device) => device.deviceId === id);
    if (isDeviceAvailable) {
      this.selectedVideoId = id;
    } else {
      const firstAvailableDevice = this.devices.find((device) => device.kind === 'videoinput');
      this.selectedVideoId = firstAvailableDevice.deviceId;
    }
  };

  /**
   * Method for setting mic in jink library
   * @param {string} deviceId
   */
  setMicChange = (audioDeviceId) => {
    this.messagingService.emit(SET_BROADCASTER_VIDEO_LOADING, true);

    this.jinkInstance.updateStream({
      video: { deviceId: { exact: this.selectedVideoId } },
      audio: { deviceId: { exact: audioDeviceId } },
    }).then(() => {
      this.messagingService.emit(SET_BROADCASTER_VIDEO_LOADING, false);
    }).catch(() => {
      this.messagingService.emit(SET_BROADCASTER_VIDEO_LOADING, false);
    });
  };

  /**
   * Method for muting audio
   */
  muteAudio = () => {
    this.jinkInstance.muteAudio();
  };

  /**
   * Method for unmuting audio
   */
  unmuteAudio = () => {
    this.jinkInstance.unmuteAudio();
  };

  retryStreamPush = (protocol, provider, originId, sourceType, retryDelay) => {
    this.triesLeft -= 1;

    return new Promise((resolve) => {
      setTimeout(async() => {
        await this.initStreamingRoom({ protocol, provider, originId, sourceType });
        resolve();
      }, retryDelay);
    });
  };

  /**
   * Method for initialization streaming room
   * @param {string} protocol STREAM_PROTOCOL
   * @param {string} provider STREAM_PROVIDERS
   * @param {string} originId
   * @param {string} sourceType External or WebRTC
   * @emits INIT_STREAMING_ROOM_RESPONSE emits the response data for initialization room
   */
  initStreamingRoom = async({ protocol, provider, originId, sourceType, switchingStreamingProvider }) => {
    const currentTimestamp = new Date().getTime();
    this.provider = provider;
    const isLastInitWasRecently = currentTimestamp - this.lastInitTimestamp < (2 * MINUTE_AS_MILLISECONDS);

    if (isLastInitWasRecently) {
      this.slowInternetRetriesLeft -= 1;
    }

    if (StreamingStore.isStreamOver) {
      return;
    }

    const { user: { username, accessHash } } = UserStore;
    const isViewerBroadcast = CookieStorageService.get('isViewerBroadcast');
    const preferredOriginValue = CookieStorageService.get('preferredOrigin');
    const params = {
      username,
      accessHash,
      isViewerBroadcast: Boolean(isViewerBroadcast),
      protocol,
      provider,
      switchingStreamingProvider,
    };

    if (sourceType === STREAM_SOURCE_EXTERNAL) {
      params.source = STREAM_SOURCE_EXTERNAL;
    }

    if (originId || preferredOriginValue !== '') {
      params.preferredOrigin = originId || preferredOriginValue;
    }

    this.protocol = protocol;
    this.provider = provider;
    this.sourceType = sourceType;
    this.originId = originId;

    if (this.triesLeft && this.slowInternetRetriesLeft) {
      try {
        const res = await ApiService.post(INIT_STREAMING_ROOM_URL, { params });
        clearTimeout(this.errorTimeout);
        this.messagingService.emit(INIT_STREAMING_ROOM_RESPONSE, res.data);
        if (protocol === STREAM_PROTOCOL.RTMP) {
          this.messagingService.emit(SET_STREAMING_JWT_TOKEN, res.data?.input?.payload?.broadcasterJwt);
        }

        try {
          await this.jinkInstance.stopPush();
        } catch {
          // skip
        }

        try {
          await this.jinkInstance?.startPush?.(res.data);

          if (!this.successfullyInitiated) {
            StreamingStore.setRoomDisconnected(false);
            this.lastInitTimestamp = currentTimestamp;
          }

          this.successfullyInitiated = true;
        } catch (error) {
          if (this.triesLeft) {
            const retryDelay = 3000;
            await this.retryStreamPush(protocol, provider, null, sourceType, retryDelay);
          } else {
            StreamingStore.setBroadcastOver(true);
            ModalService.showModal(ModalService.MODAL_KEYS.BROADCAST_ERROR_MODAL);
            track({
              eventname: 'streamPushFailed',
              provider,
              origin: originId,
              username,
              logLevel: 'ERROR',
            });
          }
        }

        if (!this.jinkInstance || StreamingStore.isStreamOver) {
          return;
        }

        this.jinkInstance.addListener('RemoteStreamStopped', () => {
          if (
            !FirebaseStore.roomStateOnline &&
            !StreamingStore.goLiveDelayed &&
            StreamingStore.isPreLive &&
            UserStore.hasCF(allUserGroups.STREAMING_SAAS)
          ) {
            ModalService.showModal(ModalService.MODAL_KEYS.BROADCAST_ERROR_MODAL, {
              show: true,
              text: 'v2broadcast.error.inactivity',
            });
          }
        });

        // FIXME: This listener generates memory leaks in some cases. Please refactor!
        this.jinkInstance.addListener('StreamStats', (_, payload) => {
          if (StreamingStore.isStreamOver) {
            this.jinkInstance.removeListener('StreamStats');
          }

          const metrics = {
            viewerFps: payload.fps,
            resolution: `${payload.width}x${payload.height}`,
            broadcasterBitrate: Math.round(payload.bitrate),
          };
          const HD = 720;
          this.messagingService.emit(BROADCASTING_IN_HD, payload.height >= HD);
          this.messagingService.emit(SET_FPS_FROM_JINK, payload.fps);
          this.messagingService.emit(UPDATE_VIDEO_METRICS, metrics);
        });
      } catch {
        if (this.triesLeft) {
          const retryDelay = 3000;
          await this.retryStreamPush(protocol, provider, originId, sourceType, retryDelay);
        } else {
          StreamingStore.setBroadcastOver(true);
          const timeout = 10000;
          this.errorTimeout = setTimeout(
            () => {
              ModalService.showModal(
                ModalService.MODAL_KEYS.BROADCAST_ERROR_MODAL,
                { text: 'v2broadcast.stream.connection.lost.modal' }
              );
            },
            timeout
          );
          track({
            eventname: 'streamInitFailed',
            provider,
            origin: originId,
            username,
            logLevel: 'ERROR',
          });
        }
      }
    } else {
      StreamingStore.setBroadcastOver(true);
      ModalService.showModal(
        ModalService.MODAL_KEYS.BROADCAST_ERROR_MODAL,
        { text: 'v2broadcast.stream.connection.lost.modal' }
      );
      track({
        eventname: 'streamInitFailed',
        provider,
        origin: originId,
        username,
        logLevel: 'ERROR',
      });
    }
  };

  clearErrorTimeout = () => {
    clearTimeout(this.errorTimeout);
  };

  destructor() {
    this.messagingService = null;
  }

  /**
   * Method for opening broadcast window
   */
  openBroadcastWindow = () => {
    const openDate = new Date();
    if (this.broadcasterWindow === null || this.broadcasterWindow.closed) {
      const height = isOpera ? BROADCAST_WINDOW_HEIGHT_OPERA : BROADCAST_WINDOW_HEIGHT;
      this.broadcasterWindow = window.open(
        `${routeDefinitions.broadcaster}?r=${openDate.valueOf()}`,
        'cam4Brd',
        `resizable=yes,height=${height},width=${BROADCAST_WINDOW_WIDTH}`
      );
    } else {
      this.broadcasterWindow.focus();
    }
    if (
      !this.broadcasterWindow || typeof this.broadcasterWindow.closed === 'undefined'
    ) {
      window.location = `${routeDefinitions.broadcaster}?r=${openDate.valueOf()}`;
    }
  };

  requestEndBroadcast = async(username, accessHash, streamUuid) => {
    this.triesLeft = 10;
    this.slowInternetRetriesLeft = 3;
    const params = { username, accessHash, roomname: username, streamUuid };

    try {
      await ApiService.delete(BROADCAST_URL, { params });
      this.messagingService.emit(RESET_SHOW_STATE);
    } catch {
      this.messagingService.emit(END_BROADCAST_ERROR);
    }
  };

  /**
   * Method for getting user broadcast settings
   * @param {string} username
   * @param {string} accessHash
   * @emits USER_BROADCAST_SETTINGS_RESPONSE emits the response data for broadcast settings
   * @emits SET_TAGS_FROM_BROADCASTER_SETTINGS_AND_GET_PREDEFINED_TAGS emits the event for fetching predefined tags
   */
  getUserBroadcastSettings = (username, accessHash) => {
    const params = { username, accessHash };

    ApiService.get(USER_BROADCAST_SETTINGS_URL, { params })
      .then((response) => {
        const tags = response.data.details.statusMessage.split(' #').slice(1);
        this.messagingService.emit(USER_BROADCAST_SETTINGS_RESPONSE, response.data);
        this.videoIdFromBroadcastSettings = response.data.camera;
        this.audioIdFromBroadcastSettings = response.data.mic;
        this.qualityFactor = response.data.details.qualityFactor;
        this.messagingService.emit(SET_TAGS_FROM_BROADCASTER_SETTINGS_AND_GET_PREDEFINED_TAGS, {
          tags,
          username,
          accessHash,
        });
      })
      .catch(() => {
        this.messagingService.emit(USER_BROADCAST_SETTINGS_ERROR);
        this.messagingService.emit(SET_TAGS_FROM_BROADCASTER_SETTINGS_AND_GET_PREDEFINED_TAGS, {
          tags: [],
          username,
          accessHash,
        });
      });
  };

  toggleBroadcasterAudio = () => {
    // WIP
  };

  /**
   * Method for starting broadcast
   * @param {string} username
   * @param {string} accessHash
   * @emits START_BROADCAST_RESPONSE emits the response data for starting broadcast
   */
  startBroadcast = async(username, accessHash, details, isViewerBroadcast, isBroadcastRestart) => {
    details.cameraName = this.selectedVideoId;

    let c2cAble = UserStore.hasCF(allUserGroups.CAM2CAM_BROADCASTER);
    const isExternalEncoder = StreamingStore.broadcastSettings.streamSource === STREAM_SOURCE_EXTERNAL;
    if (isExternalEncoder) {
      c2cAble = UserStore.hasCF(allUserGroups.CAM2CAM_OBS);
    }

    const data = qs.stringify({
      username,
      accessHash,
      details: JSON.stringify({ ...details, isViewerBroadcast, streamingProvider: this.provider }),
      c2cAble,
    });
    const headers = {
      'content-type': 'application/x-www-form-urlencoded',
      ...TracingService.generateHeaders(),
    };

    try {
      const res = await ApiService.post(BROADCAST_URL, { data, headers });
      this.messagingService.emit(START_BROADCAST_RESPONSE, {
        broadcastId: res.data.broadcastId,
        username,
        accessHash,
        isBroadcastRestart,
      });
      StreamingStore.handleStartBroadcastStatus(true);
    } catch {
      StreamingStore.handleStartBroadcastStatus(false);
      this.messagingService.emit(ROOM_LOADING_STATUS, APIStates.ERROR);
    }
  };

  /**
   * Method for setting user broadcast settings
   * @param {object} data
   * @param {string} username
   * @param {string} accessHash
   */
  setBroadcasterSettings = (data, username, accessHash) => {
    const BROADCAST_SETTINGS_URL = `${WEBCHAT_HOST}userBroadcastSettings`;
    const postData = {
      details: JSON.stringify(data),
      username,
      accessHash,
    };
    const headers = { 'content-type': 'application/x-www-form-urlencoded' };

    ApiService.post(BROADCAST_SETTINGS_URL, {
      data: qs.stringify(postData),
      headers,
    });
  };

  /**
   * Method for checking if the user has more than one approved identities
   * @param {string} username
   * @emits USER_HAS_MULTIPLE_IDENTITIES holding boolean value
   */

  getUserIdentities = (username) => {

    const IDENTITIES_URL = `${BASE_URL}/rest/v1.0/accounts/{performerName}/identities`;
    const URL = IDENTITIES_URL.replace('{performerName}', encodeURIComponent(username));

    ApiService.get(URL)
      .then(({ status, data }) => {
        let hasMultipleIdentities = true;
        if (status === StatusCodes.OK && Array.isArray(data) && data.length) {
          hasMultipleIdentities = data.length > 1;
        }

        this.messagingService.emit(USER_HAS_MULTIPLE_IDENTITIES, hasMultipleIdentities);
      })
      .catch((err) => {
        if (err.response.status === StatusCodes.NOT_FOUND) {
          this.messagingService.emit(USER_HAS_MULTIPLE_IDENTITIES, true);
        }
        parseCatch(err);
      });
  };

  externalNodeChildAdded = (username, callbackFnc) => {
    const externalNode = FirebaseService.FirebaseMain.child(firebaseNode.EXTERNAL_ENCODER_STREAM_SYNC).child(
      username.toLowerCase()
    );

    return externalNode.on('child_added', (snapshot) => callbackFnc(snapshot));
  };

  externalNodeChildRemoved = (username, callbackFnc) => {
    const externalNode = FirebaseService.FirebaseMain.child(firebaseNode.EXTERNAL_ENCODER_STREAM_SYNC).child(
      username.toLowerCase()
    );

    return externalNode.on('child_removed', () => callbackFnc());
  };

  removeExternalEncoderListener = (username) => {
    const externalNode = FirebaseService.FirebaseMain.child(firebaseNode.EXTERNAL_ENCODER_STREAM_SYNC).child(
      username.toLowerCase()
    );

    return externalNode.off();
  };

  /**
   * Method for getting stream key
   * @param {string} username
   * @emits STREAM_KEY_RESPONSE emits the response data for stream key
   */
  getStreamKey = (username, accessHash) => {
    const params = { username, accessHash };
    ApiService.get(STREAM_KEY_URL, { params })
      .then((response) => {
        this.messagingService.emit(STREAM_KEY_RESPONSE, response.data);
      })
      .catch((err) => {
        parseCatch(err);
      });
  };

  /**
   * Method for reseting stream key
   * @param {string} username
   * @emits STREAM_KEY_RESPONSE emits the response data for stream key
   */

  resetStreamKey = (username, accessHash) => {
    const params = { username, accessHash };
    ApiService.post(STREAM_KEY_URL, { params })
      .then((response) => {
        this.messagingService.emit(STREAM_KEY_RESPONSE, response.data);
      })
      .catch((err) => {
        parseCatch(err);
      });
  };

  /**
   * Method mannualy pausing or resuming the broadcast
   * @param {string} username
   * @param {string} string, should be PAUSE or RESUME
   * @emits PAUSE_STATUS_SUCCESS if the update is successfull
   * @emits PAUSE_STATUS_ERROR when the action has an unexpected value or the request fails
   */
  updatePauseStatus = (username, accessHash, action) => {
    if (!PAUSE_BROADCAST_ACTIONS[action]) {
      const error = 'Unexpected pause action';
      this.messagingService.emit(PAUSE_STATUS_ERROR, error);
      parseCatch(error);

      return;
    }

    const params = { username, accessHash, action };

    ApiService.post(PAUSE_BROADCAST_URL, { params })
      .then(() => {
        this.messagingService.emit(PAUSE_STATUS_SUCCESS);
      })
      .catch((err) => {
        this.messagingService.emit(PAUSE_STATUS_ERROR, err);
        parseCatch(err);
      });
  };

  getLeaderboard = (username, accessHash, tippingSessionId) => {
    // eslint-disable-next-line max-len
    const LEADERBOARD_URL = `${TIPPING_BASE_URL}/tipper/reporting/lboard.php?user_id=${username}&key=${accessHash}&session_id=${tippingSessionId}&v=2`;

    ApiService.get(LEADERBOARD_URL).then((response) => {
      if (response.data && Array.isArray(response.data)) {
        this.messagingService.emit(TIPPERS_LEADERBOARD_DATA, response.data);
      }
    });
  };

  inviteToRoom = (username, accessHash, originalUsername) => {
    const data = qs.stringify({
      roomname: username,
      username,
      accessHash,
      type: 'joinInvite',
      recipient: originalUsername,
    });
    const headers = { 'content-type': 'application/x-www-form-urlencoded' };

    ApiService.post(SEND_NOTIFICATION_URL, { data, headers })
      .then(() => {
        NotificationService.create({
          type: NotificationService.type.TOAST,
          text: translate('v2broadcast.invite.to.room.success'),
          subtype: NotificationService.subtype.SUCCESS,
        });
      })
      .catch(() =>
        NotificationService.create({
          type: NotificationService.type.TOAST,
          text: translate('v2broadcast.invite.to.room.success'),
          subtype: NotificationService.subtype.SUCCESS,
        })
      );
  };
}

export default new StreamingService();
